/**
 * What each WorkshopsCalendar view covers: the range it asks its caller to
 * fetch, which workshops fall inside it, and the labels that name it.
 *
 * Month and Week cover a calendar period around the anchor. List does not: it
 * is the workshops still to come, from now, over a fixed window ahead — so
 * nothing in it has already started, and it never needs a date navigator.
 */
import { WORKSHOP_RANGE_MAX_DAYS } from '@bonsai/shared';

import { viewerDate } from '../../../utils/viewer-date';

import {
  addDaysUtc,
  chipDayKey,
  MONTHS_FULL,
  MONTHS_SHORT,
  startOfWeekUtc,
  utcDayKey,
  utcYearMonth,
  viewerDayUtc,
  type WeekStartsOn,
} from './workshops-calendar-dates';
import type { CalendarView, WorkshopChip } from './workshopsCalendarTypes';

/** How far before the viewer's today the List reaches, so a late class in a
 *  zone behind the viewer's is still listed while it has not started. */
const LIST_LOOKBACK_DAYS = 1;

/** How far ahead the List reaches: the most one request to the workshop
 *  range endpoints can ask for. */
export const LIST_WINDOW_DAYS = WORKSHOP_RANGE_MAX_DAYS;

/** The dates to fetch, `YYYY-MM-DD` with both ends included: the displayed
 *  period, or for List the window ahead of the viewer's today. */
export function fetchRange(
  view: CalendarView,
  anchor: Date,
  weekStartsOn: WeekStartsOn,
  now: Date,
): { from: string; to: string } {
  const span = (first: Date, last: Date) => ({ from: utcDayKey(first), to: utcDayKey(last) });
  if (view === 'List') {
    // The lookback comes out of the window rather than on top of it, so the
    // span stays within the cap.
    return {
      from: viewerDate(-LIST_LOOKBACK_DAYS, now),
      to: viewerDate(LIST_WINDOW_DAYS - LIST_LOOKBACK_DAYS - 1, now),
    };
  }
  if (view === 'Week') {
    const start = startOfWeekUtc(anchor, weekStartsOn);
    return span(start, addDaysUtc(start, 6));
  }
  // Month: the full month grid, so adjacent-month cells are populated.
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const last = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));
  const gridStart = startOfWeekUtc(first, weekStartsOn);
  return span(gridStart, addDaysUtc(startOfWeekUtc(last, weekStartsOn), 6));
}

/** The last day the List's window covers, as "Sep 22". */
export function listWindowThrough(now: Date): string {
  const last = addDaysUtc(viewerDayUtc(now), LIST_WINDOW_DAYS - LIST_LOOKBACK_DAYS - 1);
  return `${MONTHS_SHORT[last.getUTCMonth()]} ${last.getUTCDate()}`;
}

/** "June 2026" in Month view; "Jun 1 – 7" (or cross-month) in Week view. */
export function periodLabel(view: CalendarView, anchor: Date, weekStartsOn: WeekStartsOn): string {
  if (view === 'Month') return `${MONTHS_FULL[anchor.getUTCMonth()]} ${anchor.getUTCFullYear()}`;
  const start = startOfWeekUtc(anchor, weekStartsOn);
  const end = addDaysUtc(start, 6);
  const sm = MONTHS_SHORT[start.getUTCMonth()];
  return start.getUTCMonth() === end.getUTCMonth()
    ? `${sm} ${start.getUTCDate()} – ${end.getUTCDate()}`
    : `${sm} ${start.getUTCDate()} – ${MONTHS_SHORT[end.getUTCMonth()]} ${end.getUTCDate()}`;
}

/**
 * Does the workshop belong in what the view is showing?
 *
 * Month and Week compare the chip's VENUE-LOCAL day key (BON-386) against the
 * displayed period's calendar-day keys, so this agrees with where the grid
 * buckets the chip. List compares the absolute instant against now: a
 * workshop that has started, anywhere, is no longer upcoming.
 */
export function inDisplayedPeriod(
  w: WorkshopChip,
  view: CalendarView,
  anchor: Date,
  weekStartsOn: WeekStartsOn,
  now: Date,
): boolean {
  if (view === 'List') return new Date(w.date).getTime() >= now.getTime();
  const dayKey = chipDayKey(w);
  if (view === 'Week') {
    const start = startOfWeekUtc(anchor, weekStartsOn);
    return dayKey >= utcDayKey(start) && dayKey < utcDayKey(addDaysUtc(start, 7));
  }
  return dayKey.slice(0, 7) === utcYearMonth(anchor);
}

// The count line: "X workshops in <Month>" (Month), "X workshops this week"
// (Week), nothing (List). Counts the visible (selected-subregion) workshops
// that fall in the displayed period.
export function periodCountLabel(
  view: CalendarView,
  anchor: Date,
  weekStartsOn: WeekStartsOn,
  visible: WorkshopChip[],
  now: Date,
): string | null {
  if (view === 'List') return null;
  const n = visible.filter((w) => inDisplayedPeriod(w, view, anchor, weekStartsOn, now)).length;
  return view === 'Week'
    ? `${n} workshops this week`
    : `${n} workshops in ${MONTHS_FULL[anchor.getUTCMonth()]}`;
}
